// Bolsa de compras del catálogo público. Vive en el celular de la clienta
// (localStorage, una por negocio): si cierra la página, al volver sigue ahí.
// Solo guarda { id, cantidad }; nombre, foto y precio salen siempre del catálogo
// cargado (y al enviar, la BD toma el precio real de Kontrol).
import { precioEfectivo } from './precios';

export const MAX_PRENDAS = 30;
export const MAX_CANTIDAD = 10;

const clave = (negocioId) => `kontrol_bolsa_${negocioId}`;
const claveFav = (negocioId) => `kontrol_favoritos_${negocioId}`;

const leer = (k) => {
  try { const v = JSON.parse(localStorage.getItem(k) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
const escribir = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sin almacenamiento: vive en memoria */ } };

const limpiarCantidad = (n) => Math.min(MAX_CANTIDAD, Math.max(1, Math.trunc(Number(n)) || 1));

export function leerBolsa(negocioId) {
  return leer(clave(negocioId))
    .filter((x) => x && typeof x.id === 'string')
    .map((x) => ({ id: x.id, cantidad: limpiarCantidad(x.cantidad) }))
    .slice(0, MAX_PRENDAS);
}
export const guardarBolsa = (negocioId, bolsa) => escribir(clave(negocioId), bolsa);

export function agregar(bolsa, id, cantidad = 1) {
  const ya = bolsa.find((x) => x.id === id);
  if (ya) return bolsa.map((x) => (x.id === id ? { ...x, cantidad: limpiarCantidad(x.cantidad + cantidad) } : x));
  if (bolsa.length >= MAX_PRENDAS) return bolsa;
  return [...bolsa, { id, cantidad: limpiarCantidad(cantidad) }];
}
export const cambiarCantidad = (bolsa, id, cantidad, max = MAX_CANTIDAD) =>
  bolsa.map((x) => (x.id === id ? { ...x, cantidad: Math.min(limpiarCantidad(cantidad), Math.max(1, max)) } : x));

// Cuántas piezas puede pedir de un producto: lo que hay en inventario
// (productos_publicos.disponibles, tope 10). Prendas únicas → 1.
export const maximoDe = (p) => (Number.isFinite(p?.disponibles) && p.disponibles >= 1 ? Math.min(p.disponibles, MAX_CANTIDAD) : MAX_CANTIDAD);
export const quitar = (bolsa, id) => bolsa.filter((x) => x.id !== id);

// Une la bolsa con los productos del catálogo. Lo que ya no está disponible
// (vendido o sacado del catálogo) sale aparte para avisarle a la clienta.
export function lineasBolsa(bolsa, productos) {
  const porId = new Map((productos || []).map((p) => [p.id, p]));
  const lineas = []; const noDisponibles = [];
  bolsa.forEach((x) => {
    const p = porId.get(x.id);
    if (p && (p.stock || 0) >= 1) {
      const max = maximoDe(p);
      const cantidad = Math.min(x.cantidad, max); // nunca más de lo que hay
      lineas.push({ ...x, cantidad, max, producto: p, subtotal: precioEfectivo(p) * cantidad });
    }
    else noDisponibles.push(x.id);
  });
  return { lineas, noDisponibles };
}

export const totalBolsa = (lineas) => lineas.reduce((s, l) => s + l.subtotal, 0);
export const piezasBolsa = (lineas) => lineas.reduce((s, l) => s + l.cantidad, 0);

// Mensaje para confirmar el pedido con la tienda por WhatsApp.
// envio: número (0 = gratis) o null (se coordina). total = solo las prendas.
// `enlacePedido` (/pd/<grupo>): un solo enlace cuya vista previa trae las fotos
// de TODAS las prendas (WhatsApp solo previsualiza el primer enlace). Con él ya
// no se pone un enlace por prenda.
export function mensajeBolsa({ negocioNombre, lineas, total, ahorro = 0, envio = null, nombre, telefono, entrega, direccion, pago, nota, simbolo, enlace, enlacePedido }) {
  const fmt = (n) => `${simbolo} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const detalle = (p) => [p.talla && `Talla ${p.talla}`, p.color].filter(Boolean).join(' · ');
  const filas = lineas.map((l) => {
    const d = detalle(l.producto);
    return `▪ ${l.cantidad} × ${l.producto.nombre}${d ? ` (${d})` : ''} — ${fmt(l.subtotal)}${l.combo ? ` 🎁 ${l.combo}` : ''}` +
      (enlace && !enlacePedido ? `\n   ${enlace(l.producto)}` : '');
  });
  if (enlacePedido) filas.push(`\n📸 Mi pedido con fotos:\n${enlacePedido}`);
  return `¡Hola ${negocioNombre || 'tienda'}! 🛍️ Quiero hacer este pedido:\n\n${filas.join('\n')}\n\n` +
    (ahorro > 0 ? `🎁 Ahorro por combo: ${fmt(ahorro)}\n` : '') +
    (typeof envio === 'number'
      ? `Subtotal: ${fmt(total)}\nEnvío: ${envio > 0 ? fmt(envio) : 'gratis'}\nTotal: ${fmt(total + envio)}\n\n`
      : `Total aprox: ${fmt(total)}${entrega && /domicilio/i.test(entrega) ? ' + envío (por coordinar)' : ''}\n\n`) +
    `Nombre: ${nombre}\nWhatsApp: ${telefono}` +
    (entrega ? `\nEntrega: ${entrega}` : '') +
    (direccion ? `\nDirección: ${direccion}` : '') +
    (pago ? `\nPago: ${pago}` : '') +
    (nota ? `\nNota: ${nota}` : '');
}

// Favoritos ❤️ (también solo en el celular de la clienta).
export const leerFavoritos = (negocioId) => leer(claveFav(negocioId)).filter((x) => typeof x === 'string').slice(0, 200);
export const guardarFavoritos = (negocioId, favs) => escribir(claveFav(negocioId), favs);
export const alternarFavorito = (favs, id) => (favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id]);

// "BRASIER STRAPLE" → "Brasier Straple" (se ve más cuidado en la tienda).
export function nombreBonito(nombre) {
  const s = String(nombre || '').trim();
  if (!s || s !== s.toUpperCase()) return s;
  return s.toLowerCase().replace(/(^|[\s(/-])(\p{L})/gu, (_, a, b) => a + b.toUpperCase());
}

// Búsqueda sin acentos ni mayúsculas en nombre, marca, color, talla y tipo.
const sinAcentos = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export function coincideBusqueda(p, texto) {
  const palabras = sinAcentos(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return true;
  const todo = sinAcentos([p.nombre, p.marca, p.color, p.talla, p.subtipo, p.categoria, p.codigo].join(' '));
  return palabras.every((w) => todo.includes(w));
}
